/**
 * API для отслеживания посещений сайта
 * Отправляет уведомления о посещениях в Telegram канал
 */

import { API_BASE_URL } from './chatConfig';

/**
 * Отправка уведомления о посещении страницы
 * @param {Object} visitData - Данные о посещении
 * @param {string} visitData.page - Путь страницы (например, "/pricing")
 * @param {string} visitData.referrer - Источник перехода (опционально)
 * @param {string} visitData.userAgent - User Agent браузера (опционально)
 * @returns {Promise<Object>} Результат отправки уведомления
 */
export const sendVisitNotification = async (visitData = {}) => {
  try {
    const payload = {
      page: visitData.page || window.location.pathname,
      referrer: visitData.referrer || document.referrer || undefined,
      userAgent: visitData.userAgent || navigator.userAgent || undefined,
    };

    const response = await fetch(`${API_BASE_URL}/visits/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Visit notification sent:', data);
    return data;
  } catch (error) {
    console.error('Error sending visit notification:', error);
    // Не прерываем работу приложения при ошибке отправки уведомления
    return { success: false, error: error.message };
  }
};

/**
 * Отправка уведомления о посещении с дополнительной информацией
 * @param {string} page - Путь страницы
 * @param {Object} options - Дополнительные опции
 * @returns {Promise<Object>} Результат отправки уведомления
 */
export const trackPageVisit = async (page, options = {}) => {
  const visitData = {
    page,
    referrer: options.referrer || document.referrer,
    userAgent: options.userAgent || navigator.userAgent,
  };

  return sendVisitNotification(visitData);
};

/**
 * Отправка уведомления о первом посещении сайта
 * Проверяет localStorage, чтобы не отправлять повторно
 * @returns {Promise<Object>} Результат отправки уведомления
 */
export const trackFirstVisit = async () => {
  const hasVisited = localStorage.getItem('hasVisited');
  
  if (!hasVisited) {
    localStorage.setItem('hasVisited', 'true');
    localStorage.setItem('firstVisitTime', new Date().toISOString());
    
    return sendVisitNotification({
      page: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
    });
  }
  
  return { success: false, message: 'Already visited' };
};

/**
 * Получение информации о текущем визите
 * @returns {Object} Информация о визите
 */
export const getVisitInfo = () => {
  return {
    page: window.location.pathname,
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    hasVisitedBefore: !!localStorage.getItem('hasVisited'),
    firstVisitTime: localStorage.getItem('firstVisitTime'),
  };
};
